import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import CountUp from "react-countup";
import { ChartBar, Smiley, Clock, SealCheck } from "@phosphor-icons/react";

const figures = [
    {
        icon: ChartBar,
        type: "count",
        start: 0,
        end: 500,
        prefix: "+",
        suffix: "",
        label: "Audits et études réalisés",
    },
    {
        icon: Smiley,
        type: "count",
        start: 0,
        end: 98,
        prefix: "",
        suffix: "%",
        label: "Clients satisfaits",
    },
    {
        icon: Clock,
        type: "text",
        display: "24-48h",
        label: "Rapports d'audit",
    },
    {
        icon: SealCheck,
        type: "text",
        display: "RGE",
        label: "Certification reconnue",
    },
];

function useInView(threshold = 0.25) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => e.isIntersecting && setInView(true),
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

export default function KeyFigures() {
    const [ref, inView] = useInView(0.2);

    return (
        <section
            data-testid="key-figures-section"
            ref={ref}
            className="relative py-24 lg:py-28 bg-white border-y border-lumos-border overflow-hidden"
        >
            {/* premium gold accent line */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-px bg-gradient-to-r from-transparent via-lumos-amber to-transparent opacity-60" />
            <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-lumos-blue/[0.04] blur-3xl rounded-full" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
                <div className="text-center mb-14">
                    <span className="eyebrow justify-center">L&apos;expertise en chiffres</span>
                    <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl text-lumos-ink leading-tight">
                        Une exigence,<span className="text-lumos-blue"> mesurable.</span>
                    </h2>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12 lg:gap-x-10 relative">
                    {figures.map((f, i) => {
                        const Icon = f.icon;
                        return (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 24 }}
                                animate={inView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.2, 0.7, 0.2, 1] }}
                                className="relative flex flex-col items-center text-center group"
                                data-testid={`figure-${i}`}
                            >
                                {/* divider */}
                                {i > 0 && (
                                    <div className="hidden lg:block absolute -left-5 top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-lumos-border to-transparent" />
                                )}

                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-lumos-blue/15 to-lumos-blue/5 grid place-items-center text-lumos-blue mb-5 transition-all duration-500 group-hover:scale-110 group-hover:shadow-[0_10px_30px_-10px_rgba(0,175,255,0.45)]">
                                    <Icon size={26} weight="duotone" />
                                </div>

                                <div className="font-display text-5xl lg:text-6xl xl:text-7xl font-extrabold text-lumos-ink tracking-tight leading-[0.95]">
                                    {f.type === "text" ? (
                                        <span className={f.display.length > 4 ? "text-4xl lg:text-5xl xl:text-6xl" : ""}>
                                            {f.display}
                                        </span>
                                    ) : (
                                        <>
                                            {f.prefix}
                                            {inView ? (
                                                <CountUp start={f.start} end={f.end} duration={2.2} separator="" />
                                            ) : (
                                                f.start
                                            )}
                                            {f.suffix}
                                        </>
                                    )}
                                </div>
                                <div className="mt-4 text-sm lg:text-base text-lumos-muted leading-snug max-w-[200px] font-medium">
                                    {f.label}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
